import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { SubscriptionGate } from '@/components/paywall/SubscriptionGate';
import { sheetGateCopy } from '@/lib/paywall';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { FlashCardDeck } from '@/components/flashcards/FlashCardDeck';
import { LessonBar } from '@/components/sheets/LessonBar';
import { TermItem } from '@/components/sheets/TermItem';
import { TermSheetViewToggle, type TermSheetView } from '@/components/sheets/TermSheetViewToggle';
import { InlineMarkdown } from '@/components/InlineMarkdown';
import { cardAudio, localCardGroups, localCards, type LearningCard } from '@/content/mobile';
import { localResources } from '@/content/resources';
import { SHEET_INTROS } from '@/content/sheet-intros';
import { useCatalogResource } from '@/hooks/useLearnerCatalog';
import { getLocalFavorites, setLocalFavorites } from '@/lib/account-sync';
import { learnerApiJson } from '@/lib/learner-api';
import { isPremiumRequiredError, shouldHidePremiumContent } from '@/lib/premium-gate';
import { useLearnerPreferences } from '@/providers/LearnerPreferences';
import { useLearnerAuth } from '@/providers/LearnerAuth';
import { usePremiumAccess } from '@/hooks/usePremiumAccess';
import { track } from '@/lib/analytics';
import { playAudio } from '@/lib/play-audio';

type CloudTerm = {
  title: string;
  translations: Record<string, string>;
  subtitle_translations: Record<string, string>;
  sound_url: string | null;
  sound_children_url: string | null;
  persistency_key: string;
};
type CloudBlock = {
  id: number;
  block_type: string;
  title_translations: Record<string, string>;
  body_translations: Record<string, string>;
  terms: CloudTerm[];
};
type CloudSheet = { name: string; requires_subscription: boolean; blocks: CloudBlock[] };

const FRAME_CYCLE = ['GREEN', 'LIGHT_BLUE', 'YELLOW', 'LIGHT_ORANGE', 'LIGHT_RED', 'LIGHT_PURPLE'];

type SheetSearch = { terms?: string; family?: string };

export const Route = createFileRoute('/app/sheets/$slug')({
  component: SheetPage,
  validateSearch: (search: Record<string, unknown>): SheetSearch => ({
    terms: typeof search.terms === 'string' ? search.terms : undefined,
    family: typeof search.family === 'string' ? search.family : undefined,
  }),
});

function localized(map: Record<string, string> | undefined, language: string): string {
  return map?.[language] || map?.en || '';
}

export function SheetPage() {
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const preferences = useLearnerPreferences();
  const auth = useLearnerAuth();
  const premium = usePremiumAccess();
  const [view, setView] = useState<TermSheetView>('list');
  const [index, setIndex] = useState(0);
  const [favorites, setFavorites] = useState(getLocalFavorites);
  const mode = preferences.dialect === 'moob' ? 'moob' : 'hmonglus';
  const catalogResource = useCatalogResource(
    '/v1/term-sheets',
    mode,
    preferences.baseLanguage,
    'Grammar',
    '/app/sheets',
    slug,
  );
  const cloud = useQuery({
    queryKey: ['term-sheet', slug],
    queryFn: () => learnerApiJson<CloudSheet>(auth?.getToken ?? null, `/v1/term-sheets/${encodeURIComponent(slug)}`),
    retry: false,
  });
  const local = useMemo(() => {
    if (slug === 'useful-terms' && search.terms === 'phrases') {
      return localCards(slug, preferences.dialect, preferences.baseLanguage, {
        keyIncludes: 'PHRASE',
      });
    }
    if (slug === 'useful-terms' && search.terms === 'words') {
      return localCards(slug, preferences.dialect, preferences.baseLanguage, {
        keyIncludes: 'WORD',
        keyExcludes: 'PHRASE',
      });
    }
    if (slug === 'word-families' && search.family) {
      const family = search.family.toLowerCase();
      const group = localCardGroups(slug, preferences.dialect, preferences.baseLanguage).find((item) =>
        item.key.toLowerCase().includes(family),
      );
      if (group?.cards.length) return group.cards;
      return localCards(slug, preferences.dialect, preferences.baseLanguage).filter((card) =>
        card.title.toLowerCase().startsWith(family),
      );
    }
    return localCards(slug, preferences.dialect, preferences.baseLanguage);
  }, [preferences.baseLanguage, preferences.dialect, search.family, search.terms, slug]);
  const paymentRequired = isPremiumRequiredError(cloud.error);
  const localResource = localResources.find((item) => {
    if (item.slug !== slug) return false;
    if (search.terms) return item.href.includes(`terms=${search.terms}`);
    if (search.family) return item.href.includes(`family=${search.family}`);
    return !item.href.includes('?');
  });
  const resource = catalogResource ?? localResource;
  const title = resource?.title || cloud.data?.name || slug;
  const gated = shouldHidePremiumContent({
    flaggedPremium: Boolean(
      catalogResource?.premium || localResource?.premium || cloud.data?.requires_subscription,
    ),
    paymentRequired,
    isSubscribed: premium.isSubscribed,
  });

  const cloudTerms: LearningCard[] = useMemo(
    () =>
      cloud.data?.blocks.flatMap((block) =>
        block.terms.map((term, termIndex) => ({
          title: term.title,
          translation: localized(term.translations, preferences.baseLanguage),
          subtitle: localized(term.subtitle_translations, preferences.baseLanguage) || undefined,
          sound: term.sound_url,
          soundChildren: term.sound_children_url,
          persistencyKey: term.persistency_key,
          cardColor: FRAME_CYCLE[termIndex % FRAME_CYCLE.length],
        })),
      ) ?? [],
    [cloud.data, preferences.baseLanguage],
  );
  const terms = cloudTerms.length ? cloudTerms : local;
  const intro = SHEET_INTROS[slug];

  const playTerm = (term: LearningCard) => {
    const source = cardAudio(term, preferences.voice);
    if (source) void playAudio(source);
    track('Term Sheet Audio Press', { sheet: slug, term: term.title });
  };

  const close = () => {
    track('Screen Close Button Press', { Screen: title });
    void navigate({ to: '/app/resources' });
  };

  if (gated) {
    const gate = sheetGateCopy(slug);
    return (
      <SubscriptionGate
        source={gate.source}
        content={slug}
        message={gate.message}
        heroEmoji={gate.heroEmoji}
        onDismiss={() => void navigate({ to: '/app/resources' })}
      />
    );
  }

  if (view === 'flashcards' && terms.length) {
    const card = index < terms.length ? terms[index] : undefined;
    return (
      <FlashCardDeck
        cards={terms}
        title={title}
        index={index}
        onIndexChange={setIndex}
        favorites={favorites}
        onToggleFavorite={() => {
          if (!card) return;
          const active = favorites.includes(card.persistencyKey);
          const next = active
            ? favorites.filter((key) => key !== card.persistencyKey)
            : [...favorites, card.persistencyKey];
          setFavorites(next);
          setLocalFavorites(next);
        }}
        onPlayAudio={() => {
          if (card) playTerm(card);
        }}
        canPlayAudio={Boolean(card && cardAudio(card, preferences.voice))}
        onExit={() => setView('list')}
      />
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-6 pb-24 pt-5">
      <LessonBar
        title={title}
        onClose={close}
        right={terms.length ? <TermSheetViewToggle view={view} onChange={setView} /> : null}
      />

      {cloud.data?.blocks.map((block) => {
        if (block.block_type === 'intro' || block.block_type === 'prose') {
          const body = localized(block.body_translations, preferences.baseLanguage);
          if (!body) return null;
          return (
            <InlineMarkdown
              key={block.id}
              text={body}
              as="p"
              className="mb-4 mt-4 whitespace-pre-line font-nunito text-base leading-6 text-[#1E1E1E]"
            />
          );
        }
        if (block.block_type === 'section_header') {
          const heading = localized(block.title_translations, preferences.baseLanguage);
          if (!heading) return null;
          return (
            <h2 key={block.id} className="mb-2.5 mt-5 font-nunito text-lg font-bold text-[#1E1E1E]">
              {heading}
            </h2>
          );
        }
        if (block.block_type === 'divider') return <div key={block.id} className="h-3" />;
        if (block.block_type === 'term_list') {
          return (
            <div key={block.id} className="mt-1">
              {block.terms.map((term) => {
                const card: LearningCard = {
                  title: term.title,
                  translation: localized(term.translations, preferences.baseLanguage),
                  subtitle: localized(term.subtitle_translations, preferences.baseLanguage) || undefined,
                  sound: term.sound_url,
                  soundChildren: term.sound_children_url,
                  persistencyKey: term.persistency_key,
                };
                return (
                  <TermItem
                    key={term.persistency_key}
                    term={term.title}
                    definition={card.translation}
                    extra={card.subtitle}
                    hasAudio={Boolean(cardAudio(card, preferences.voice))}
                    onPlay={() => playTerm(card)}
                  />
                );
              })}
            </div>
          );
        }
        const body = localized(block.body_translations, preferences.baseLanguage);
        if (!body) return null;
        return (
          <p key={block.id} className="mb-4 mt-4 whitespace-pre-line font-nunito text-base leading-6 text-[#1E1E1E]">
            {body}
          </p>
        );
      })}

      {!cloud.data && intro ? (
        <p className="mb-4 mt-4 font-nunito text-base leading-6 text-[#1E1E1E]">{intro}</p>
      ) : null}

      {!cloud.data && local.length
        ? local.map((term) => (
            <TermItem
              key={term.persistencyKey}
              term={term.title}
              definition={term.translation}
              extra={term.subtitle}
              hasAudio={Boolean(cardAudio(term, preferences.voice))}
              onPlay={() => playTerm(term)}
            />
          ))
        : null}

      {!cloud.data && !local.length && cloud.isLoading ? <p className="mt-8">Loading lesson…</p> : null}
      {!cloud.data && !local.length && !cloud.isLoading ? (
        <p className="mt-8 rounded-2xl bg-amber-50 p-5">This lesson does not contain term rows yet.</p>
      ) : null}
    </div>
  );
}

